import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { Toaster } from "@/components/ui/sonner";
import { StagingBadge } from "@/components/StagingBadge";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthPage from "@/pages/AuthPage";
import CookModePage from "@/pages/CookModePage";
import DashboardPage from "@/pages/DashboardPage";
import GroceryPage from "@/pages/GroceryPage";
import LandingPage from "@/pages/LandingPage";
import { PrivacyPage, RefundPolicyPage, TermsPage } from "@/pages/LegalPages";
import LeftoversPage from "@/pages/LeftoversPage";
import PantryPage from "@/pages/PantryPage";
import PasswordResetPage from "@/pages/PasswordResetPage";
import PlannerPage from "@/pages/PlannerPage";
import ProfilePage from "@/pages/ProfilePage";
import RecipesPage from "@/pages/RecipesPage";


const AppLoading = () => {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(255,209,102,0.35),_transparent_35%),linear-gradient(180deg,_#fbf7ef_0%,_#f4efe3_45%,_#edf3ea_100%)] px-6" style={{ minHeight: "100dvh", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="w-full max-w-sm rounded-[30px] border border-stone-200/70 bg-white/85 p-8 text-left shadow-[0_30px_80px_rgba(44,62,38,0.12)] backdrop-blur">
        <p className="text-xs font-semibold uppercase tracking-[0.32em] text-emerald-700" data-testid="app-loading-kicker">
          Smartly Meals
        </p>
        <h1 className="mt-4 text-3xl font-semibold text-stone-900" data-testid="app-loading-title">
          Planner wordt geladen
        </h1>
        <div className="mt-6 h-2 overflow-hidden rounded-full bg-stone-200" data-testid="app-loading-progress-track">
          <div className="h-full w-2/3 animate-pulse rounded-full bg-emerald-600" data-testid="app-loading-progress-bar" />
        </div>
      </div>
    </div>
  );
};


const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return null;
};


const ProtectedRoute = ({ children }) => {
  const { loading, user } = useAuth();

  if (loading) {
    return <AppLoading />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};


const GuestRoute = ({ children }) => {
  const { loading, user } = useAuth();

  if (loading) {
    return <AppLoading />;
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};


const RootRoute = () => {
  const { loading, user } = useAuth();

  if (loading) {
    return <AppLoading />;
  }

  return user ? <DashboardPage /> : <LandingPage />;
};


const AppRoutes = () => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/login" element={<GuestRoute><AuthPage mode="login" /></GuestRoute>} />
        <Route path="/signup" element={<GuestRoute><AuthPage mode="signup" /></GuestRoute>} />
        <Route path="/forgot-password" element={<GuestRoute><PasswordResetPage mode="request" /></GuestRoute>} />
        <Route path="/reset-password" element={<GuestRoute><PasswordResetPage mode="reset" /></GuestRoute>} />
        <Route path="/pricing" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/" element={<RootRoute />} />
        <Route path="/planner" element={<ProtectedRoute><PlannerPage /></ProtectedRoute>} />
        <Route path="/cook/:itemId" element={<ProtectedRoute><CookModePage /></ProtectedRoute>} />
        <Route path="/leftovers" element={<ProtectedRoute><LeftoversPage /></ProtectedRoute>} />
        <Route path="/recipes" element={<ProtectedRoute><RecipesPage /></ProtectedRoute>} />
        <Route path="/grocery" element={<ProtectedRoute><GroceryPage /></ProtectedRoute>} />
        <Route path="/pantry" element={<ProtectedRoute><PantryPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster position="top-center" />
      <StagingBadge />
    </BrowserRouter>
  );
};


function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
